import type { PropsWithChildren, ReactElement } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useBreakpoints } from '@xing-com/crate-jobs-hooks';

import { FormField } from './form-fields.common.styles';
import type { FormFieldPropsWithCharacterLimit } from './types';

const ONE_EXTRA_CHAR_LEFT = -1;

const StyledFormField = styled(FormField)`
  p:last-of-type {
    text-align: right;
  }
`;

export const CommentsField = <T extends FieldValues>({
  control,
  path,
  onFocus,
  characterLimit,
}: PropsWithChildren<FormFieldPropsWithCharacterLimit<T>>): ReactElement => {
  const intl = useIntl();
  const { isMediaSmallOrTiny } = useBreakpoints();

  return (
    <Controller
      name={path}
      control={control}
      render={({ field }) => {
        const charsLeft = characterLimit - field.value.length;
        return (
          <StyledFormField
            variant="plain"
            multiline
            size="large"
            minRows={isMediaSmallOrTiny ? 7 : 4}
            {...field}
            aria-label={intl.formatMessage({
              id: 'JOBS_INSTANT_APPLY_FORM_ADDITIONAL_COMMENTS_HEADLINE',
            })}
            error={charsLeft < 0}
            onFocus={onFocus}
            helperText={
              charsLeft < 0
                ? intl.formatMessage(
                    {
                      id:
                        charsLeft === ONE_EXTRA_CHAR_LEFT
                          ? 'JOBS_INSTANT_APPLY_FORM_ADDITIONAL_COMMENTS_FIELD_ERROR_ONE'
                          : 'JOBS_INSTANT_APPLY_FORM_ADDITIONAL_COMMENTS_FIELD_ERROR',
                    },
                    {
                      number: Math.abs(charsLeft),
                    }
                  )
                : `${intl.formatMessage(
                    {
                      id: 'JOBS_INSTANT_APPLY_FORM_ADDITIONAL_COMMENTS_FIELD_HELPER',
                    },
                    {
                      number: charsLeft,
                    }
                  )}`
            }
          />
        );
      }}
      rules={{
        maxLength: characterLimit,
      }}
    />
  );
};
